import type { ResolvedAuxiliaryScreen, ResolvedModule } from '../model/resolve-navigation';

/**
 * Every place the command palette can take you, and the match it runs — both pure, both derived
 * from the SAME navigation model the rail is built from. A destination the rail cannot show is a
 * destination the palette must not offer, and deriving both from one resolved list is what
 * guarantees that without a second gate check.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. The standard asks the palette to "navigate, find entities,
 * run actions". This covers navigation only, and the proposal says why: the navigation half is
 * mechanical, while "find an entity" needs a cross-module search read that does not exist, and "run
 * an action" needs a per-module contribution model that is an open decision. A palette that offers
 * a Search section returning nothing would be worse than one that is honestly only a navigator.
 * `CommandDestination` is the shape those contributions would extend.
 */
export type CommandDestination = {
  readonly id: string;
  /** What the row reads: the destination's own name. */
  readonly label: string;
  /** The module it belongs to, shown as context — absent for a module row itself. */
  readonly group?: string;
  readonly path: string;
};

export function buildCommandDestinations(
  modules: readonly ResolvedModule[],
  auxiliaryScreens: readonly ResolvedAuxiliaryScreen[],
): readonly CommandDestination[] {
  const destinations: CommandDestination[] = [];

  for (const module of modules) {
    destinations.push({ id: `module:${module.id}`, label: module.label, path: module.path });
    for (const section of module.sections) {
      destinations.push({
        id: `section:${module.id}:${section.id}`,
        label: section.label,
        group: module.label,
        path: section.path,
      });
    }
  }

  for (const screen of auxiliaryScreens) {
    destinations.push({ id: `screen:${screen.id}`, label: screen.label, path: screen.basePath });
  }

  return destinations;
}

/**
 * Diacritic- and case-insensitive substring match on the label, and on the group so that typing
 * `finanza` reaches every Finance view rather than only the module row.
 *
 * The fold matters in Italian: a person types `attivita` and the label reads `Attività`. `NFD` plus
 * a combining-marks strip is the whole mechanism — no dependency, and it is the same normalisation
 * both sides of the comparison get.
 */
export function foldForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('it');
}

export function filterCommandDestinations(
  destinations: readonly CommandDestination[],
  query: string,
): readonly CommandDestination[] {
  const needle = foldForSearch(query.trim());
  if (needle.length === 0) {
    return destinations;
  }
  return destinations.filter((destination) => {
    const haystack = foldForSearch(
      destination.group === undefined
        ? destination.label
        : `${destination.label} ${destination.group}`,
    );
    return haystack.includes(needle);
  });
}
